import {
  Button,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  IconButtonVariant,
  Modal,
  Show
} from "shared/components";
import { Cancel01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import type {
  InstructionBulkApply,
  InstructionBulkApplyOutcome
} from "types/landlord/address";
import { unitLabel, type PropertyDictionary } from "./helpers";

const REASON_LABELS: Record<string, string> = {
  pms_locked: "Locked by the PMS — instructions can't be changed here",
  not_found: "Not found or not eligible for bulk apply",
  matches_template: "Already matches its own template's instructions",
  validation_failed: "Validation error"
};

const getReasonText = (outcome: InstructionBulkApplyOutcome) =>
  outcome.message || REASON_LABELS[outcome.reason] || outcome.reason;

const getOutcomeName = (
  outcome: InstructionBulkApplyOutcome,
  dictionary: PropertyDictionary
) => outcome.unit_title || `${dictionary.Unit} #${outcome.unit_id}`;

const getTitle = ({
  jobFailedOutright,
  hasFailures
}: {
  jobFailedOutright: boolean;
  hasFailures: boolean;
}) => {
  if (jobFailedOutright) return "Could not apply instructions";
  if (hasFailures) return "Instructions applied with some errors";

  return "Instructions applied";
};

const getDescription = ({
  result,
  jobFailedOutright,
  dictionary
}: {
  result: InstructionBulkApply;
  jobFailedOutright: boolean;
  dictionary: PropertyDictionary;
}) => {
  if (jobFailedOutright)
    return (
      result.error ||
      "Something went wrong while applying the instructions. Please try again."
    );

  const parts = [
    `Updated ${result.updated_count} ${unitLabel(result.updated_count, dictionary)}.`
  ];

  if (result.skipped_count > 0)
    parts.push(
      `Skipped ${result.skipped_count} ${unitLabel(result.skipped_count, dictionary)}.`
    );

  if (result.failed_count > 0)
    parts.push(
      `Failed on ${result.failed_count} ${unitLabel(result.failed_count, dictionary)}.`
    );

  return parts.join(" ");
};

const OutcomeList = ({
  heading,
  outcomes,
  reasonClassName,
  dictionary
}: {
  heading: string;
  outcomes: InstructionBulkApplyOutcome[];
  reasonClassName: string;
  dictionary: PropertyDictionary;
}) => (
  <Show if={outcomes.length > 0}>
    <div className="space-y-2">
      <p className="text-xs font-medium text-content-400">{heading}</p>
      {outcomes.map((outcome) => (
        <div key={`${heading}-${outcome.unit_id}`}>
          <p className="truncate text-sm font-medium text-content-700">
            {getOutcomeName(outcome, dictionary)}
          </p>
          <p className={`text-xs ${reasonClassName}`}>
            {getReasonText(outcome)}
          </p>
        </div>
      ))}
    </div>
  </Show>
);

export const ApplyUnitInstructionsResultDialog = ({
  result,
  onClose
}: {
  result: InstructionBulkApply | null;
  onClose: () => void;
}) => {
  const dictionary = useUnitsPropertiesDictionary();

  if (!result) return null;

  const jobFailedOutright = result.status === "failed";
  const hasFailures = result.failed.length > 0;
  const hasDetails = hasFailures || result.skipped.length > 0;

  return (
    <Modal onClose={onClose} opened panelClassName="max-w-[520px] w-full">
      <header className="border-b border-alpha-40 px-6 py-6">
        <div className="flex items-start justify-between gap-4">
          <h3 className="text-xl font-bold text-content-1000">
            {getTitle({ jobFailedOutright, hasFailures })}
          </h3>
          <IconButton
            HugeIcon={Cancel01Icon}
            onClick={onClose}
            size={IconButtonSize.BASE}
            variant={IconButtonVariant.TEXT_BASE}
          />
        </div>
        <p className="mt-2 text-sm text-content-400">
          {getDescription({ result, jobFailedOutright, dictionary })}
        </p>
      </header>

      <Show if={hasDetails}>
        <div className="max-h-[280px] space-y-4 overflow-y-auto border-b border-alpha-40 px-6 py-4">
          <OutcomeList
            dictionary={dictionary}
            heading="Failed"
            outcomes={result.failed}
            reasonClassName="text-danger-500"
          />
          <OutcomeList
            dictionary={dictionary}
            heading="Skipped"
            outcomes={result.skipped}
            reasonClassName="text-content-400"
          />
        </div>
      </Show>

      <footer className="flex items-center justify-end px-6 py-4">
        <Button onClick={onClose} variant={ButtonVariant.PRIMARY}>
          Done
        </Button>
      </footer>
    </Modal>
  );
};
